import { message } from 'antd';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { storageApi } from '../services/storageApi';
import { RootState } from '../store';
import { StorageMetadata } from '../types/storage.types';

interface UseFileUploadOptions {
  onSuccess?: (key: string) => void;
  onError?: (error: Error) => void;
  generateKey?: (file: File, metadata: StorageMetadata) => string;
}

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const {username} = useSelector((state: RootState) => state.auth);

  const defaultGenerateKey = (file: File, metadata: StorageMetadata) => {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(7);
    const sellerId = metadata.sellerId;
    const deviceId = metadata.deviceId;
    return `${sellerId}/${deviceId}/${metadata.fileName}`;
  };

  const generateKey = options.generateKey || defaultGenerateKey;

  const uploadFile = async (file: File, metadata: StorageMetadata): Promise<string | null> => {
    try {
      setUploading(true);
      setProgress(0);

      const key = generateKey(file, metadata);
      await storageApi.uploadFileWithUrl(key, file);

      setProgress(100);
      options.onSuccess?.(key);
      return key;
    } catch (error) {
      const err = error as Error;
      message.error('Failed to upload file: ' + err.message);
      options.onError ? options.onError(err) : onError(err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (key: string) => {
    try {
      await storageApi.deleteFile(key);
    } catch (error) {
      const err = error as Error;
      message.error('Failed to delete file: ' + err.message);
      throw err;
    }
  };

  const onError = (error: Error) => {
    message.error('Failed to upload file');
    console.error(error);
  };

  return {
    uploadFile,
    deleteFile,
    uploading,
    progress,
    onError
  };
}; 